import { forwardRef, useRef, useImperativeHandle, useState, useEffect } from 'react';
import { HiChevronLeft } from 'react-icons/hi';

import { Modal, ModalMethods, Button, toasts } from '../../../../../ui-component';
import { api } from '../../../../../utils/apiRequest';
import { getValueFromLocalStorage } from '../../../../../utils/common';
import type { StitchOptionApi } from './OptionsModal';

type MultiLineOptionModalProps = {
  atomicOutfitId: number | null;
  existingOption: StitchOptionApi | null;
  onSaved: () => void;
  onClose: () => void;
};

const MultiLineOptionModal = forwardRef<ModalMethods, MultiLineOptionModalProps>(
  ({ atomicOutfitId, existingOption, onSaved, onClose }, ref) => {
    const modalRef = useRef<ModalMethods>(null);
    const [fieldLabel, setFieldLabel] = useState('');
    const [placeholder, setPlaceholder] = useState('');
    const [maxLength, setMaxLength] = useState<number>(0);
    const [price, setPrice] = useState<string>('');
    const [isSaving, setIsSaving] = useState(false);

    useImperativeHandle(ref, () => ({
      show: () => modalRef.current?.show(),
      hide: () => modalRef.current?.hide(),
      toggle: () => modalRef.current?.toggle(),
      getModalState: () => modalRef.current?.getModalState() ?? false,
    }));

    useEffect(() => {
      if (existingOption) {
        setFieldLabel(existingOption.key);
        setPlaceholder(existingOption.values?.[0]?.field_label || 'You can enter any text here');
        setMaxLength(0);
        const existingPrice = existingOption.values?.[0]?.price;
        setPrice(
          existingPrice !== undefined && existingPrice !== null ? String(existingPrice) : ''
        );
      } else {
        setFieldLabel('');
        setPlaceholder('You can enter any text here');
        setMaxLength(0);
        setPrice('');
      }
    }, [existingOption]);

    const handleSave = async () => {
      const boutique_id = getValueFromLocalStorage('boutique_id');

      if (!boutique_id || !atomicOutfitId) {
        toasts('error', 'Boutique or piece not selected', 'multiline-option-save-error');

        return;
      }

      const trimmedLabel = fieldLabel.trim();

      if (!trimmedLabel) {
        toasts('error', 'Enter field label', 'multiline-option-label-error');

        return;
      }

      setIsSaving(true);
      try {
        const payload = {
          atomic_outfit_id: atomicOutfitId,
          multi_line_stitch_option_request: {
            field_label: trimmedLabel,
            field_type: 3,
            placeholder: placeholder.trim() || 'You can enter any text here',
            max_length: Number(maxLength) || 0,
            price: Number(price) || 0,
          },
        };

        if (existingOption) {
          const response = await api.putRequest(
            `new-stitch-option/stitch-options/${existingOption.id}/v2`,
            payload
          );

          if (response.status) {
            toasts('success', 'Option updated successfully', 'multiline-option-update-success');
            modalRef.current?.hide();
            onSaved();
          } else {
            toasts(
              'error',
              response.message || 'Failed to update option',
              'multiline-option-update-error'
            );
          }
        } else {
          const response = await api.postRequest(
            `new-stitch-option/${boutique_id}/boutique_stitch_options`,
            payload
          );

          if (response.status) {
            toasts('success', 'Option added successfully', 'multiline-option-add-success');
            modalRef.current?.hide();
            onSaved();
          } else {
            toasts(
              'error',
              response.message || 'Failed to add option',
              'multiline-option-add-error'
            );
          }
        }
      } catch (err) {
        if (err instanceof Error) {
          toasts('error', err.message, 'multiline-option-save-error');
        }
      } finally {
        setIsSaving(false);
      }
    };

    const headerComponent = (
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => {
            modalRef.current?.hide();
            onClose();
          }}
          className="p-1 rounded hover:bg-gray-100 flex items-center gap-1"
          aria-label="Back"
        >
          <HiChevronLeft size={24} />
          <span className="font-inter text-sm">Back</span>
        </button>
        <div className="flex items-center gap-2">
          <span className="w-10 h-10 rounded-lg border-2 border-blue-500 flex items-center justify-center font-semibold text-blue-600 text-sm">
            ABC
          </span>
          <span className="font-inter font-semibold text-lg">Multi line field</span>
        </div>
      </div>
    );

    const footerComponent = (
      <Button
        label="Save"
        size="medium"
        bgColor="primary"
        onClick={handleSave}
        disabled={isSaving}
        className="flex items-center gap-2"
      />
    );

    return (
      <Modal
        ref={modalRef}
        size="xl"
        headerComponent={headerComponent}
        footerComponent={footerComponent}
        showFooter={true}
        showCloseIcon={false}
      >
        <div className="flex flex-col gap-4 py-4">
          <div>
            <label className="font-inter text-sm font-medium text-gray-700 block mb-1">
              Field label
            </label>
            <input
              type="text"
              value={fieldLabel}
              onChange={(e) => setFieldLabel(e.target.value)}
              placeholder="Enter field label"
              className="w-full font-inter border-b border-gray-300 py-2 px-0 focus:outline-none focus:border-blue-500"
            />
          </div>
          <div>
            <label className="font-inter text-sm font-medium text-gray-700 block mb-1">
              Placeholder text
            </label>
            <textarea
              value={placeholder}
              onChange={(e) => setPlaceholder(e.target.value)}
              placeholder="You can enter any text here"
              rows={4}
              className="w-full font-inter border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <div>
            <label className="font-inter text-sm font-medium text-gray-700 block mb-1">
              Additional price (optional)
            </label>
            <div className="relative mt-1">
              <span className="absolute inset-y-0 left-0 pl-3 flex items-center text-gray-500 text-sm">
                ₹
              </span>
              <input
                type="number"
                min={0}
                value={price}
                onChange={(e) => setPrice(e.target.value)}
                placeholder="0"
                className="w-full font-inter border border-gray-300 rounded-lg pl-7 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <p className="mt-1 text-xs text-gray-500 font-inter">
              This price will be added to the outfit when this option is selected.
            </p>
          </div>
        </div>
      </Modal>
    );
  }
);

MultiLineOptionModal.displayName = 'MultiLineOptionModal';

export default MultiLineOptionModal;
